import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import bg2 from '@/assets/bg8.webp';
import ggbooklet from '@/assets/ggbooklet.webp';
import configure from '@/assets/logo_g.png';
import phoneguide from '@/assets/phoneguide.png';
import computerguide from '@/assets/computerguide.png';
import usermanual from '@/assets/usermanual.png';
import {
  createUpdateSubscription,
  checkDiscountCode,
  type DiscountCodeDetails,
} from '@/hooks/useStripe';
import { getCookie } from '@/lib/cookies';
import { getCurrencySymbol, type CurrencyCode } from '@/lib/currency';
import { calculateFamilyPrice, calculateIndividualPrice } from '@/lib/familyUpgradePricing';
import { useUser as useClerkUser } from '@clerk/clerk-react';
import { ArrowRight, Check, CircleHelp, Minus, Plus, Tag } from 'lucide-react';
import { FaUser } from 'react-icons/fa';

const KIT_PRICE = 4.99;

// A discount carried in from a campaign (e.g. the quiz funnel: /subscribe?discount=DETECTOR)
// is stashed here so it survives the sign-in / sign-up / onboarding round trip and is
// re-applied when the visitor lands back on this page, even if the URL param is lost.
const PENDING_DISCOUNT_KEY = 'gg_pending_discount';

function readStoredDiscount(): string {
  if (typeof window === 'undefined') return '';
  try {
    return (window.localStorage.getItem(PENDING_DISCOUNT_KEY) || '').trim();
  } catch {
    return '';
  }
}

function storeDiscount(code: string) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PENDING_DISCOUNT_KEY, code.trim().toUpperCase());
  } catch {
    /* storage unavailable (private mode, etc.) */
  }
}

function clearStoredDiscount() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(PENDING_DISCOUNT_KEY);
  } catch {
    /* storage unavailable */
  }
}

export const Route = createFileRoute('/subscribe')({
  validateSearch: (search: Record<string, unknown>) => ({
    plan: typeof search.plan === 'string' ? search.plan : undefined,
    billing: typeof search.billing === 'string' ? search.billing : undefined,
    discount: typeof search.discount === 'string' ? search.discount : undefined,
  }),
  component: RegisterDetails,
});

function RegisterDetails() {
  const search = Route.useSearch();
  const { user: clerkUser, isLoaded: clerkLoaded } = useClerkUser();
  const currencyCookie = (typeof document !== 'undefined' ? getCookie('gg_currency') : null) as CurrencyCode | null;
  const currencySymbol = getCurrencySymbol(currencyCookie ?? 'USD');
  const initialPlanType: 'personal' | 'sponsor' = search.plan === 'family' || search.plan === 'sponsor' ? 'sponsor' : 'personal';
  const initialBilling: 'month' | 'year' = search.billing === 'annual' || search.billing === 'year' ? 'year' : 'month';
  const [planType, setPlanType] = useState<'personal' | 'sponsor'>(initialPlanType);
  const [billingPeriod, setBillingPeriod] = useState<'month' | 'year'>(initialBilling);
  const [userCount, setUserCount] = useState(1);
  const [hasKit, setHasKit] = useState(false);
  const [error, setError] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [showPromo, setShowPromo] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState<DiscountCodeDetails | null>(null);
  const [discountError, setDiscountError] = useState('');
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
  const [isStarterKitInfoOpen, setIsStarterKitInfoOpen] = useState(false);

  const isSponsorPlan = planType === 'sponsor';
  const recurringBeforeDiscount = isSponsorPlan
    ? calculateFamilyPrice(userCount, billingPeriod === 'year')
    : calculateIndividualPrice(billingPeriod === 'year');
  const totalKit = hasKit ? KIT_PRICE : 0;
  const recurringBeforeCents = Math.round(recurringBeforeDiscount * 100);
  const discountCents = (() => {
    if (!appliedDiscount) return 0;
    if (!appliedDiscount.active || !appliedDiscount.valid) return 0;
    if (appliedDiscount.minimumAmount && recurringBeforeCents < appliedDiscount.minimumAmount) return 0;
    if (appliedDiscount.amountOff && appliedDiscount.amountOff > 0) {
      return Math.min(appliedDiscount.amountOff, recurringBeforeCents);
    }
    if (appliedDiscount.percentOff && appliedDiscount.percentOff > 0) {
      return Math.floor((recurringBeforeCents * appliedDiscount.percentOff) / 100);
    }
    return 0;
  })();
  const totalRecurringAfterDiscount = Math.max(0, (recurringBeforeCents - discountCents) / 100);
  const totalToday = totalRecurringAfterDiscount + totalKit;

  const updateSubscription = createUpdateSubscription();
  const checkCodeMutation = checkDiscountCode();
  const didAutoApply = useRef(false);

  // The code carried in from a campaign, whether via the URL or stashed earlier.
  const pendingCode = (search.discount || readStoredDiscount() || '').trim().toUpperCase();
  // While we're validating a carried-in code (and haven't applied or failed yet).
  const validatingDiscount = checkCodeMutation.status === 'pending' && !appliedDiscount && !discountError;

  // Stash a URL discount the moment we see it, so it survives the auth round trip.
  useEffect(() => {
    if (search.discount && search.discount.trim()) {
      storeDiscount(search.discount.trim());
    }
  }, [search.discount]);

  // Auto-apply discount code from URL or storage (once only).
  // MUST be before early returns — hooks must be called unconditionally.
  useEffect(() => {
    if (!clerkUser) return;
    if (typeof window === 'undefined') return;
    if (didAutoApply.current) return;
    const params = new URLSearchParams(window.location.search);
    const fromUrl = (search.discount || params.get('discount') || readStoredDiscount() || '').trim();
    if (fromUrl) {
      didAutoApply.current = true;
      setCouponCode(fromUrl.toUpperCase());
      setShowPromo(true);
      const applyCode = async () => {
        try {
          const details = await checkCodeMutation.mutateAsync(fromUrl.trim().toUpperCase());
          const isExpired = details.expiresAt !== null && (details.expiresAt * 1000) <= Date.now();
          if (isExpired || !details.active || !details.valid) {
            setDiscountError(isExpired ? 'Expired discount code' : 'Invalid discount code');
            return;
          }
          setAppliedDiscount(details);
          setCouponCode((details.code || fromUrl).toUpperCase());
          setShowPromo(true);
        } catch {
          setDiscountError('Invalid discount code');
        }
      };
      applyCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clerkUser]);

  if (!clerkLoaded) {
    return (
      <div style={{ backgroundImage: `url(${bg2})` }} className="min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat" />
    );
  }

  if (!clerkUser) {
    // Keep the discount on the URL we send people back to, even if it only lived in storage.
    const redirectSearch =
      (typeof window !== 'undefined' ? window.location.search : '') ||
      (pendingCode ? `?discount=${encodeURIComponent(pendingCode)}` : '');
    return (
      <div style={{ backgroundImage: `url(${bg2})` }} className="min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-sm w-full">
          <CheckoutSteps current={1} />
          <h2 className="text-xl font-semibold text-slate-900 mb-2 text-center">Create your account</h2>
          <p className="text-base text-slate-500 mb-6 text-center">First, sign in or sign up. Then choose your plan.</p>

          {pendingCode ? (
            <div className="mb-5 flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-3 py-2.5 text-[0.95rem] text-green-800">
              <Tag className="h-4 w-4 shrink-0" />
              <span>
                Your code <span className="font-semibold">{pendingCode}</span> is saved. We'll apply it
                automatically once you're in.
              </span>
            </div>
          ) : null}

          <a
            href={`/signup?redirectTo=${encodeURIComponent('/subscribe' + redirectSearch)}`}
            className="inline-flex items-center justify-center w-full py-3 px-4 text-base font-medium text-white bg-violet-600 rounded-xl hover:bg-violet-700 transition-colors"
          >
            Sign up or sign in
          </a>
        </div>
      </div>
    );
  }

  const planLabel = isSponsorPlan ? 'Family Sponsor' : 'Personal';
  const planSubLabel = isSponsorPlan
    ? `You (sponsor, free) + ${userCount} loved ${userCount === 1 ? 'one' : 'ones'}`
    : '1 protected person (you)';
  const previewIconCount = Math.min(userCount, 4);
  const extraIconCount = Math.max(0, userCount - previewIconCount);
  const periodLabel = billingPeriod === 'year' ? 'year' : 'month';
  const formatPrice = (amount: number) => `${currencySymbol}${amount.toFixed(2)}`;

  const handleContinue = async () => {
    if (!clerkUser) return;
    setError('');
    const userLicenses: Record<string, 'FREE' | 'PAID' | number> = {};
    if (isSponsorPlan) {
      userLicenses.UNASSIGNED = userCount;
    } else {
      userLicenses[clerkUser.id] = 'PAID';
    }
    const welcomePack = hasKit ? { type: 'both', sent: false } : undefined as any;
    try {
      const codeToSend = appliedDiscount && appliedDiscount.active && appliedDiscount.valid ? (appliedDiscount.code || '').toUpperCase() : undefined;
      const result = await updateSubscription.mutateAsync({ userLicenses, welcomePack, couponCode: codeToSend, returnTo: '/subscribe', interval: billingPeriod });
      // Checkout has been created with the code attached; it's done its job.
      clearStoredDiscount();
      if (result && typeof result === 'object' && 'redirectUrl' in result && typeof result.redirectUrl === 'string') {
        window.location.assign(result.redirectUrl);
      } else if (result && 'success' in result && result.success) {
        window.location.assign('/');
      }
    } catch (err: any) {
      const serverMsg = err?.response?.data?.message
      setError(serverMsg || err?.message || 'Failed to start subscription. Please try again.');
    }
  };

  const handleApplyCoupon = async (codeArg?: string, isAutoApply?: boolean) => {
    setDiscountError('');
    setAppliedDiscount(null);
    const code = (codeArg ?? couponCode ?? '').trim().toUpperCase();
    if (!code) {
      setDiscountError('Enter a code to apply.');
      return;
    }
    try {
      if (!isAutoApply) setIsApplyingDiscount(true);
      const details = await checkCodeMutation.mutateAsync(code);
      const nowMs = Date.now();
      const isExpired = details.expiresAt !== null && (details.expiresAt * 1000) <= nowMs;
      if (isExpired) {
        setDiscountError('Expired discount code');
        return;
      }
      if (!details.active || !details.valid) {
        setDiscountError('Invalid discount code');
        return;
      }
      setAppliedDiscount(details);
      setCouponCode((details.code || code).toUpperCase());
      setShowPromo(true);
    } catch (err: any) {
      const serverMsg = err?.response?.data?.error
      const debugInfo = err?.response?.data?.debug
      const debugSuffix = debugInfo ? ` [${debugInfo.status}: ${debugInfo.originalError}]` : ''
      setDiscountError((serverMsg || 'Invalid discount code') + debugSuffix);
    } finally {
      if (!isAutoApply) setIsApplyingDiscount(false);
    }
  };

  return (
    <div style={{ backgroundImage: `url(${bg2})` }} className="min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat px-0 sm:px-6">
      <div className="w-full max-w-full sm:max-w-xl p-0 sm:p-6">
        <div className="relative overflow-visible rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 max-h-[calc(100vh-1rem)] sm:max-h-none flex flex-col">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-violet-50 via-transparent to-transparent" />
          <div className="relative flex-1 overflow-visible p-5 sm:p-8 space-y-3">
            <CheckoutSteps current={2} />

            {validatingDiscount ? (
              <div className="flex items-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-3 py-2.5 text-[0.95rem] text-violet-800" role="status">
                <Tag className="h-4 w-4 shrink-0" />
                <span>Applying your discount...</span>
              </div>
            ) : appliedDiscount && (appliedDiscount.percentOff || appliedDiscount.amountOff) ? (
              <div className="flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-3 py-2.5 text-[0.95rem] text-green-800" role="status">
                <Tag className="h-4 w-4 shrink-0" />
                <span>
                  {appliedDiscount.percentOff
                    ? `${appliedDiscount.percentOff}% off`
                    : `${currencySymbol}${(appliedDiscount.amountOff! / 100).toFixed(2)} off`}{' '}
                  applied with code{' '}
                  <span className="font-semibold">{(appliedDiscount.code || couponCode).toUpperCase()}</span>
                </span>
              </div>
            ) : pendingCode && discountError ? (
              <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-[0.95rem] text-amber-800" role="status">
                <Tag className="h-4 w-4 shrink-0" />
                <span>We couldn't apply {pendingCode}. You can still continue, or add another code below.</span>
              </div>
            ) : null}

            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center">
                <img src={configure} alt="GranGuard logo" className="h-11 w-auto object-contain" />
              </div>
              <div>
                <h1 className="text-[clamp(1.35rem,2vw,1.85rem)] font-semibold text-slate-900">Choose your plan</h1>
                <p className="text-[clamp(0.95rem,1.2vw,1.05rem)] text-slate-600 -mt-1.5">Simple pricing for individuals and families.</p>
              </div>
            </div>

            <section className="space-y-3" aria-label="Plan options">
              <div className="grid grid-cols-2 gap-2 rounded-2xl bg-slate-100 p-1" role="group" aria-label="Plan options">
                  <button
                    type="button"
                    aria-pressed={!isSponsorPlan}
                    onClick={() => setPlanType('personal')}
                    className={`w-full cursor-pointer rounded-xl border px-3 py-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 ${
                      !isSponsorPlan
                        ? 'border-violet-300 bg-white text-slate-900 shadow-sm'
                        : 'border-transparent text-slate-600 hover:bg-white/70 hover:text-slate-900'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={`inline-flex h-4 w-4 shrink-0 self-center items-center justify-center rounded-full border ${
                          !isSponsorPlan ? 'border-violet-600' : 'border-slate-300'
                        }`}
                        aria-hidden="true"
                      >
                        {!isSponsorPlan ? <span className="h-2 w-2 rounded-full bg-violet-600" /> : null}
                      </span>
                      <div className="space-y-0.5">
                        <p className="text-[clamp(1rem,1.2vw,1.12rem)] font-semibold">Personal</p>
                        <p className="text-[clamp(0.88rem,0.98vw,0.95rem)] -mt-1.5 whitespace-nowrap text-slate-600">Just you</p>
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    aria-pressed={isSponsorPlan}
                    onClick={() => {
                      setPlanType('sponsor');
                      setUserCount((current) => Math.max(current, 1));
                    }}
                    className={`w-full cursor-pointer rounded-xl border px-3 py-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 ${
                      isSponsorPlan
                        ? 'border-violet-300 bg-white text-slate-900 shadow-sm'
                        : 'border-transparent text-slate-600 hover:bg-white/70 hover:text-slate-900'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span
                        className={`inline-flex h-4 w-4 shrink-0 self-center items-center justify-center rounded-full border ${
                          isSponsorPlan ? 'border-violet-600' : 'border-slate-300'
                        }`}
                        aria-hidden="true"
                      >
                        {isSponsorPlan ? <span className="h-2 w-2 rounded-full bg-violet-600" /> : null}
                      </span>
                      <div className="space-y-0.5">
                        <p className="text-[clamp(1rem,1.2vw,1.12rem)] font-semibold">Family Sponsor</p>
                        <p className="text-[clamp(0.88rem,0.98vw,0.95rem)] -mt-1 whitespace-nowrap">
                          <span className="sm:hidden">Protect others</span>
                          <span className="hidden sm:inline ">Protect loved ones</span>
                        </p>
                      </div>
                    </div>
                  </button>
              </div>

              {isSponsorPlan ? (
                <div className="border-t border-slate-200 pt-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-slate-900">
                        How Many Others?
                      </p>
                      <div className="mt-0 flex flex-wrap items-center gap-2 text-[clamp(0.95rem,1.1vw,1.05rem)] text-slate-700">
                        <span className="inline-flex h-6 items-center text-slate-800">
                          You
                        </span>
                        <span className="text-slate-400">+</span>
                        <div className="flex -space-x-1" aria-hidden="true">
                          {Array.from({ length: previewIconCount }).map((_, idx) => (
                            <span
                              key={`loved-one-dot-${idx}`}
                              className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-violet-200 bg-violet-50"
                            >
                              <FaUser className="h-2.5 w-2.5 text-violet-700" />
                            </span>
                          ))}
                          {extraIconCount > 0 ? (
                            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full border border-violet-200 bg-violet-100 px-1 text-[10px] font-semibold text-violet-700">
                              +{extraIconCount}
                            </span>
                          ) : null}
                        </div>
                        <span>{userCount} loved {userCount === 1 ? 'one' : 'ones'} </span>
                      </div>
                    </div>
                    <div className="shrink-0">
                      <Stepper value={userCount} min={1} onChange={setUserCount} />
                    </div>
                  </div>
                </div>
              ) : null}
            </section>

            <div className="divide-y divide-slate-200 border-y border-slate-200">
              <div className="py-3">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="font-semibold text-slate-900">Annual Billing</p>
                    <div className="flex items-center gap-2 text-[clamp(0.92rem,1.05vw,1rem)] text-slate-700">
                      {billingPeriod === 'year' && (
                        <svg viewBox="0 0 16 16" className="h-4 w-4 text-emerald-600" aria-hidden="true">
                          <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.3" />
                          <path d="M4.5 8.3L6.9 10.6L11.3 6.2" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                      <span>{billingPeriod === 'year' ? 'Saving 20%' : 'Enable to save 20%'}</span>
                    </div>
                  </div>
                  <Toggle
                    checked={billingPeriod === 'year'}
                    onChange={(next) => setBillingPeriod(next ? 'year' : 'month')}
                    label="Enable annual billing"
                  />
                </div>
              </div>

              <div className="py-3">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="relative flex items-center gap-2" onMouseLeave={() => setIsStarterKitInfoOpen(false)}>
                      <p className="font-semibold text-slate-900">Starter Kit</p>
                      <button
                        type="button"
                        className="inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-full text-slate-500 transition hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
                        aria-label="Starter kit contents"
                        aria-expanded={isStarterKitInfoOpen}
                        onClick={() => setIsStarterKitInfoOpen((v) => !v)}
                        onMouseEnter={() => setIsStarterKitInfoOpen(true)}
                      >
                        <CircleHelp className="h-4 w-4" />
                      </button>
                      {isStarterKitInfoOpen && (
                        <div className="absolute right-0 bottom-full z-40 mb-2 w-[min(22rem,calc(100vw-2rem))] rounded-xl border border-slate-200 bg-white p-3 text-[clamp(0.86rem,0.98vw,0.94rem)] text-slate-700 shadow-lg">
                          <p className="mb-1 font-medium text-slate-900">What's included</p>
                          <ul className="space-y-1">
                            <li className="flex items-center gap-2">
                              <img src={ggbooklet} alt="Scam Defense Handbook" className="h-8 w-6" />
                              Scam Defense handbook
                            </li>
                            <li className="flex items-center gap-2">
                              <img src={phoneguide} alt="Phone quick guide" className="h-8 w-6" />
                              Fridge + phone quick guides
                            </li>
                            <li className="flex items-center gap-2">
                              <img src={computerguide} alt="Computer quick guide" className="h-8 w-6" />
                              Computer quick guide
                            </li>
                            <li className="flex items-center gap-2">
                              <img src={usermanual} alt="User manual" className="h-8 w-6" />
                              User manual for online tools
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-1 text-[clamp(0.92rem,1.05vw,1rem)] text-slate-700">
                      <span>{formatPrice(KIT_PRICE)}</span>
                      <span>one-time fee</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Toggle checked={hasKit} onChange={setHasKit} label="Add starter kit" />
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl bg-slate-50 p-4 space-y-2" aria-live="polite">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-[clamp(1.1rem,1.4vw,1.28rem)] text-slate-900">Summary</p>
                <p className="text-[clamp(0.78rem,0.9vw,0.86rem)] text-slate-500">{billingPeriod === 'year' ? 'Yearly billing' : 'Monthly billing'}</p>
              </div>

              <div className="space-y-1">
                <LineItem label={planLabel} value={`${formatPrice(totalRecurringAfterDiscount)}/${periodLabel}`} sublabel={planSubLabel} />
                {discountCents > 0 && (
                  <LineItem
                    label={`Discount ${appliedDiscount?.code ? `(${appliedDiscount.code.toUpperCase()})` : ''}`}
                    value={`- ${formatPrice(discountCents / 100)}/${periodLabel}`}
                    sublabel={`applies ${billingPeriod === 'year' ? 'yearly' : 'monthly'}`}
                    valueClassName="text-green-700"
                    sublabelClassName="text-green-700"
                  />
                )}
                {hasKit && <LineItem label="Starter Kit" value={formatPrice(totalKit)} />}

                <div className="pt-2 border-t border-slate-200 flex items-end justify-between gap-3">
                  <div>
                    <p className="text-[clamp(0.95rem,1.1vw,1.05rem)] font-semibold text-slate-900">Due today</p>
                    <p className="text-[clamp(0.78rem,0.95vw,0.88rem)] text-slate-500">Taxes and shipping (if any) are shown at checkout.</p>
                  </div>
                  <p className="text-[clamp(1.7rem,3vw,2.15rem)] font-semibold tracking-tight text-slate-900">{formatPrice(totalToday)}</p>
                </div>
              </div>

              <div>
                <button
                  type="button"
                  onClick={() => setShowPromo((v) => !v)}
                  className="inline-flex cursor-pointer items-center gap-2 text-[clamp(0.9rem,1vw,1rem)] font-medium text-slate-700 hover:text-slate-900"
                >
                  <Tag className="h-4 w-4" />
                  {showPromo ? 'Hide discount code' : 'Add discount code'}
                </button>

                {showPromo && (
                  <div className="mt-3">
                    <div className="flex flex-row items-stretch gap-2">
                      <label htmlFor="discount-code" className="sr-only">
                        Promo code
                      </label>
                      <input
                        id="discount-code"
                        type="text"
                        placeholder="Enter discount code"
                        className="h-10 flex-1 rounded-xl border border-slate-200 bg-white px-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-violet-500"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        autoComplete="off"
                        disabled={updateSubscription.isPending || checkCodeMutation.status === 'pending'}
                        inputMode="text"
                      />
                      <button
                        type="button"
                        className="h-10 cursor-pointer rounded-lg border border-slate-200 bg-white px-4 text-[clamp(0.9rem,1vw,1rem)] font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-60"
                        onClick={() => handleApplyCoupon()}
                        disabled={updateSubscription.isPending || isApplyingDiscount}
                      >
                        {isApplyingDiscount ? 'Applying...' : 'Apply code'}
                      </button>
                    </div>
                    {discountError && <div className="mt-1 text-[clamp(0.88rem,1vw,0.96rem)] text-red-600" role="alert">{discountError}</div>}
                    {appliedDiscount && (appliedDiscount.percentOff || appliedDiscount.amountOff) ? (
                      <div className="mt-1 text-[clamp(0.88rem,1vw,0.96rem)] text-green-700" role="status">
                        Applied {appliedDiscount.percentOff ? `${appliedDiscount.percentOff}%` : `${currencySymbol}${(appliedDiscount.amountOff! / 100).toFixed(2)}`} off
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            </div>

            {error && <div className="text-red-600 text-center" role="alert">{error}</div>}

            <div className="sticky bottom-0 bg-white/95 backdrop-blur-sm border-t border-slate-200 pt-3 pb-4">
              <button
                type="button"
                className="flex h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-2xl bg-violet-600 text-white font-semibold shadow-sm hover:bg-violet-700 active:bg-violet-800 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
                onClick={() => handleContinue()}
                disabled={updateSubscription.isPending}
              >
                {updateSubscription.isPending ? 'Preparing checkout...' : 'Continue to secure checkout'}
                <ArrowRight className="h-4 w-4" />
              </button>

              <div className="flex items-center justify-center gap-1 pt-4 text-[clamp(0.78rem,0.9vw,0.86rem)] text-slate-500">
                <Check className="h-4 w-4" />
                Secure checkout powered by Stripe
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CheckoutSteps({ current }: { current: 1 | 2 | 3 }) {
  const steps = [
    { n: 1 as const, label: 'Account' },
    { n: 2 as const, label: 'Plan' },
    { n: 3 as const, label: 'Payment' },
  ];
  return (
    <div className="flex items-center justify-center gap-2 mb-3" aria-label={`Step ${current} of 3`}>
      {steps.map((s, i) => {
        const done = s.n < current;
        const active = s.n === current;
        return (
          <div key={s.n} className="flex items-center gap-2">
            <div className="flex items-center gap-1.5">
              <span
                className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                  done
                    ? 'bg-violet-100 text-violet-600'
                    : active
                      ? 'bg-violet-600 text-white'
                      : 'bg-slate-100 text-slate-400'
                }`}
              >
                {done ? <Check className="h-3.5 w-3.5" /> : s.n}
              </span>
              <span
                className={`text-[0.85rem] font-medium ${
                  active ? 'text-slate-900' : done ? 'text-violet-600' : 'text-slate-400'
                }`}
              >
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 ? (
              <span className={`h-px w-5 ${done ? 'bg-violet-300' : 'bg-slate-200'}`} aria-hidden="true" />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

type LineItemProps = {
  label: string;
  value: string;
  sublabel?: string;
  className?: string;
  labelClassName?: string;
  valueClassName?: string;
  sublabelClassName?: string;
};

function LineItem({ label, value, sublabel, className, labelClassName, valueClassName, sublabelClassName }: LineItemProps) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className={`text-[clamp(0.9rem,1vw,1rem)] font-medium text-slate-900 ${labelClassName ?? className ?? ''}`}>{label}</p>
        {sublabel ? <p className={`text-[clamp(0.78rem,0.95vw,0.88rem)] text-slate-500 ${sublabelClassName ?? className ?? ''}`}>{sublabel}</p> : null}
      </div>
      <p className={`text-[clamp(0.9rem,1vw,1rem)] font-semibold text-slate-900 whitespace-nowrap ${valueClassName ?? className ?? ''}`}>{value}</p>
    </div>
  );
}

type ToggleProps = {
  checked: boolean;
  onChange: (next: boolean) => void;
  label?: string;
};

function Toggle({ checked, onChange, label }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label ?? 'Toggle option'}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-7 w-12 cursor-pointer items-center rounded-full transition ring-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 ${
        checked ? 'bg-violet-600 ring-violet-200' : 'bg-slate-200 ring-slate-300'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

type StepperProps = {
  value: number;
  min?: number;
  max?: number;
  onChange: (next: number) => void;
};

function Stepper({ value, min = 1, max, onChange }: StepperProps) {
  const dec = () => onChange(Math.max(min, value - 1));
  const inc = () => onChange(max ? Math.min(max, value + 1) : value + 1);
  const disableDec = value <= min;
  const disableInc = max ? value >= max : false;

  return (
    <div className="inline-flex items-center rounded-xl bg-slate-100 p-1 ring-1 ring-slate-200" role="group" aria-label="Protected people selector">
      <button
        type="button"
        onClick={dec}
        className="h-9 w-9 rounded-lg bg-white text-slate-900 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-40 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
        disabled={disableDec}
        aria-label="Decrease protected people"
      >
        <Minus className="h-4 w-4 mx-auto" />
      </button>

      <div className="px-3 text-center" aria-live="polite" aria-atomic="true">
        <p className="text-[clamp(1.35rem,1.9vw,1.6rem)] font-semibold leading-6 text-slate-900">{value}</p>
      </div>

      <button
        type="button"
        onClick={inc}
        className="h-9 w-9 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-40 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
        disabled={disableInc}
        aria-label="Increase protected people"
      >
        <Plus className="h-4 w-4 mx-auto" />
      </button>
    </div>
  );
}
